import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React from 'react';
import { FaArrowRight, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { findWithdrawel, getCharge, getMovement } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import SeeWithdrawel from 'views/access/withdrawals/components/SeeWithdrawel';
import { Balance, TypeRender } from '../balances';
import { toAmount } from 'views/access/orders/movementBreakdown';

/**
 * A pergunta que este modal existe para responder não é "quanto", que já estava na linha da
 * tabela — é "de onde veio isso". Uma taxa de spread sem o link para a venda que a gerou
 * deixa o usuário sem próximo passo.
 *
 * Todos estes tipos nascem de uma venda e guardam o id dela em `referenceId`.
 */
const FROM_MOVEMENT = [
  'MOVEMENT',
  'MOVEMENT_MAIN',
  'LOCK',
  'FEE',
  'SYSTEM_FEE',
  'SPREAD',
  'SPLIT',
  'CUSTOMER_SPLIT',
  'INSURANCE',
  'COMMISSION',
];

const FROM_WITHDRAWAL = ['WITHDRAWAL', 'WITHDRAWAL_ANTECIPATION'];

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  return (
    <Flex justify="space-between" align="baseline" gap={4} py={1}>
      <Text fontSize="sm" color={muted} whiteSpace="nowrap">
        {label}
      </Text>
      <Box fontSize="sm" fontWeight="medium" textAlign="right" minW={0}>
        {children}
      </Box>
    </Flex>
  );
};

const ShowBalance: React.FC<{ dado: Balance }> = ({ dado }) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.300');

  // Chaves distintas por origem. As três consultas dividiam `['movementInfo', referenceId]`
  // com fetchers diferentes, e no react-query v4 qual `queryFn` roda nesse caso é
  // indeterminado — os `enabled` mutuamente exclusivos só escondiam o problema.
  const { data: movementInfo } = useQuery(
    ['balanceOriginMovement', dado.referenceId],
    () => getMovement(dado.referenceId),
    { enabled: isOpen && !!dado.referenceId && FROM_MOVEMENT.includes(dado?.type) },
  );

  const { data: withdraw } = useQuery(
    ['balanceOriginWithdrawal', dado.referenceId],
    () => findWithdrawel(dado.referenceId),
    { enabled: isOpen && !!dado.referenceId && FROM_WITHDRAWAL.includes(dado?.type) },
  );

  const { data: depositCharge } = useQuery(['balanceOriginCharge', dado.referenceId], () => getCharge(dado.referenceId), {
    enabled: isOpen && !!dado.referenceId && dado?.type === 'DEPOSIT',
  });

  const amount = toAmount(dado.amount);
  const refunded = toAmount(dado.amountRefunded);
  const isThirdPartySale = movementInfo && dado?.Customer?.id !== movementInfo?.Customer?.id;
  const shareOfSale = movementInfo && toAmount(movementInfo.amount) !== 0
    ? ((100 * amount) / toAmount(movementInfo.amount)).toFixed(2)
    : null;

  return (
    <>
      <Button onClick={onOpen} size="sm" variant="outline" colorScheme="brand">
        <FaEye />
      </Button>
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Consultar lançamento</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="3xl" fontWeight="bold" lineHeight="1.1">
                {maskCurrency(dado.amount)}
              </Text>
              <HStack mt={2} spacing={2} flexWrap="wrap">
                <Badge colorScheme="gray">{TypeRender(dado.type, dado.amount) ?? dado.type}</Badge>
                <Badge colorScheme={dado.released ? 'green' : 'orange'}>
                  {dado.released ? 'Liberado' : 'Pendente'}
                </Badge>
              </HStack>
              {dado.description && (
                <Text mt={2} color={muted} fontSize="sm">
                  {dado.description}
                </Text>
              )}

              <Box mt={4}>
                <Row label="Cliente">{dado.Customer?.name}</Row>
                {dado.originName && <Row label="Origem">{dado.originName}</Row>}
                <Row label="Data de liberação">
                  {dado.releasedOn ? DateTime.fromISO(dado.releasedOn).toFormat('dd/MM/yyyy HH:mm') : '---'}
                </Row>
                {/* O backend devolve "0.00", que é string não vazia: a checagem por
                    truthiness mostrava "Valor Devolvido: R$ 0,00" em todo lançamento. */}
                {refunded > 0 && <Row label="Valor devolvido">{maskCurrency(dado.amountRefunded)}</Row>}
              </Box>

              {movementInfo && (
                <Box mt={5} bg={panelBg} borderWidth="1px" borderColor={panelBorder} borderRadius="lg" p={3}>
                  <Text fontSize="xs" color={muted} textTransform="uppercase" letterSpacing="wide">
                    Originado de
                  </Text>
                  <Flex
                    mt={2}
                    align="center"
                    justify="space-between"
                    gap={3}
                    cursor="pointer"
                    onClick={() => navigate(`/access/movement/${movementInfo?.id}`)}
                  >
                    <Box minW={0}>
                      <Text fontWeight="semibold">Venda de {maskCurrency(movementInfo?.amount)}</Text>
                      <Text fontSize="sm" color={muted}>
                        {DateTime.fromISO(movementInfo?.orderAt ?? movementInfo?.createdAt).toFormat(
                          'dd/MM/yyyy HH:mm',
                        )}
                        {isThirdPartySale && ` · ${movementInfo?.Customer?.name}`}
                      </Text>
                    </Box>
                    <Box as={FaArrowRight} color={muted} aria-hidden />
                  </Flex>
                  {shareOfSale && (
                    <Text mt={2} fontSize="xs" color={muted}>
                      Este lançamento representa {shareOfSale}% da venda.
                    </Text>
                  )}
                </Box>
              )}

              {withdraw && (
                <Box mt={5} bg={panelBg} borderWidth="1px" borderColor={panelBorder} borderRadius="lg" p={3}>
                  <Text fontSize="xs" color={muted} textTransform="uppercase" letterSpacing="wide">
                    Originado de
                  </Text>
                  <HStack mt={2} justify="space-between">
                    <Text fontWeight="semibold">Saque</Text>
                    <SeeWithdrawel data={withdraw} />
                  </HStack>
                </Box>
              )}

              {depositCharge && (
                <Box mt={5} bg={panelBg} borderWidth="1px" borderColor={panelBorder} borderRadius="lg" p={3}>
                  <Text fontSize="xs" color={muted} textTransform="uppercase" letterSpacing="wide">
                    Originado de
                  </Text>
                  <Flex
                    mt={2}
                    align="center"
                    justify="space-between"
                    gap={3}
                    cursor="pointer"
                    onClick={() => navigate(`/access/charge/${depositCharge?.id}`)}
                  >
                    <Box minW={0}>
                      <Text fontWeight="semibold">Cobrança de {maskCurrency(depositCharge?.amount)}</Text>
                      <Text fontSize="sm" color={muted}>
                        {DateTime.fromISO(depositCharge?.createdAt).toFormat('dd/MM/yyyy HH:mm')}
                      </Text>
                    </Box>
                    <Box as={FaArrowRight} color={muted} aria-hidden />
                  </Flex>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={onClose}>
                Fechar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ShowBalance;
